import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWallet } from '../contexts/WalletContext'
import { User, Link as LinkIcon, Shield, Upload, Image, Video, X } from 'lucide-react'

export default function CreateProfile() {
  const navigate = useNavigate()
  const { address, isConnected } = useWallet()
  const [formData, setFormData] = useState({
    handle: '',
    displayName: '',
    bio: '',
    links: {
      x: '',
      instagram: '',
      discord: '',
      website: ''
    },
    nsfw: false
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [profilePicture, setProfilePicture] = useState<File | null>(null)
  const [bannerMedia, setBannerMedia] = useState<File | null>(null)
  const [bannerPreview, setBannerPreview] = useState<string | null>(null)
  const [pfpPreview, setPfpPreview] = useState<string | null>(null)
  const [existingProfile, setExistingProfile] = useState<any>(null)

  const validateHandle = (handle: string): boolean => {
    return /^[a-z0-9_]{3,20}$/.test(handle)
  }

  const checkHandleAvailability = (handle: string): boolean => {
    const existingProfiles = JSON.parse(localStorage.getItem('harmonyProfiles') || '{}')
    return !existingProfiles[handle]
  }

  // Check if user already has a profile
  useEffect(() => {
    if (address) {
      const userProfileHandle = localStorage.getItem(`userProfile_${address}`)
      if (userProfileHandle) {
        const existingProfiles = JSON.parse(localStorage.getItem('harmonyProfiles') || '{}')
        const profile = existingProfiles[userProfileHandle]
        if (profile) {
          setExistingProfile(profile)
          
          // If we're editing (coming from profile page), pre-populate the form
          const urlParams = new URLSearchParams(window.location.search)
          if (urlParams.get('edit') === 'true') {
            console.log('Editing profile, loading data:', profile)
            setFormData({
              handle: profile.handle,
              displayName: profile.displayName,
              bio: profile.bio || '',
              links: profile.links || { x: '', instagram: '', discord: '', website: '' },
              nsfw: profile.nsfw || false
            })
            
            // Load existing images
            if (profile.pfpDataUrl) {
              console.log('Loading existing PFP:', profile.pfpDataUrl)
              setPfpPreview(profile.pfpDataUrl)
            }
            if (profile.bannerDataUrl) {
              console.log('Loading existing banner:', profile.bannerDataUrl)
              setBannerPreview(profile.bannerDataUrl)
            }
            
            // Clear the existing profile state so we can edit
            setExistingProfile(null)
          }
        }
      }
    }
  }, [address])

  // Debug useEffect to monitor image previews
  useEffect(() => {
    console.log('PFP Preview state:', pfpPreview)
    console.log('Banner Preview state:', bannerPreview)
  }, [pfpPreview, bannerPreview])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    const newErrors: Record<string, string> = {}
    
    if (!formData.handle) {
      newErrors.handle = 'Handle is required'
    } else if (!validateHandle(formData.handle)) {
      newErrors.handle = 'Handle must be 3-20 characters, lowercase letters, numbers, and underscores only'
    } else if (!checkHandleAvailability(formData.handle)) {
      // Check if this is the user's own handle (allow editing)
      const userProfileHandle = localStorage.getItem(`userProfile_${address}`)
      if (userProfileHandle !== formData.handle) {
        newErrors.handle = 'Handle is already taken. Please choose a different one.'
      }
    }
    
    if (!formData.displayName) {
      newErrors.displayName = 'Display name is required'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setLoading(true)
    try {
      // Create profile data object
      const profileData = {
        ...formData,
        walletAddress: address,
        createdAt: new Date().toISOString(),
        isVerified: false, // Will be verified by admin later
        stats: {
          followers: 0,
          following: 0,
          contentCount: 0,
          totalEarnings: 0
        }
      }

      // Upload profile picture if provided, or keep existing one
      let pfpWalrusId = null
      let pfpDataUrl = null
      if (profilePicture) {
        // In production, upload to Walrus storage
        // For now, create a mock Walrus ID and store data URL
        pfpWalrusId = `pfp_${Date.now()}_${profilePicture.name}`
        pfpDataUrl = pfpPreview // Store the preview data URL
        console.log('New profile picture uploaded:', pfpWalrusId)
      } else if (pfpPreview) {
        // Keep existing profile picture if no new one uploaded
        pfpDataUrl = pfpPreview
        console.log('Keeping existing profile picture')
      }

      // Upload banner if provided, or keep existing one
      let bannerWalrusId = null
      let bannerDataUrl = null
      if (bannerMedia) {
        // In production, upload to Walrus storage
        // For now, create a mock Walrus ID and store data URL
        bannerWalrusId = `banner_${Date.now()}_${bannerMedia.name}`
        bannerDataUrl = bannerPreview // Store the preview data URL
        console.log('New banner uploaded:', bannerWalrusId)
      } else if (bannerPreview) {
        // Keep existing banner if no new one uploaded
        bannerDataUrl = bannerPreview
        console.log('Keeping existing banner')
      }

      // Add media IDs and data URLs to profile data
      const finalProfileData = {
        ...profileData,
        pfpWalrusId,
        bannerWalrusId,
        pfpDataUrl,
        bannerDataUrl
      }

      // Save profile to localStorage (in production, save to blockchain/database)
      const existingProfiles = JSON.parse(localStorage.getItem('harmonyProfiles') || '{}')
      existingProfiles[formData.handle] = finalProfileData
      localStorage.setItem('harmonyProfiles', JSON.stringify(existingProfiles))

      // Also save current user's profile handle
      if (address) {
        localStorage.setItem(`userProfile_${address}`, formData.handle)
      }

      console.log('Profile created successfully:', finalProfileData)
      
      // Show success message
      setErrors({})
      
      // Simulate upload time
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      // Show success notification (you could add a toast here)
      const urlParams = new URLSearchParams(window.location.search)
      const isEdit = urlParams.get('edit') === 'true'
      
      if (isEdit) {
        alert(`✅ Profile updated successfully! Your changes have been saved.`)
      } else {
        alert(`🎉 Profile created successfully! Welcome to HarmonyDataHub, @${formData.handle}!`)
      }
      
      // Navigate to the new profile
      navigate(`/u/${formData.handle}`)
    } catch (error) {
      console.error('Error creating profile:', error)
      setErrors({ submit: 'Failed to create profile. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const handleLinkChange = (platform: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      links: {
        ...prev.links,
        [platform]: value
      }
    }))
  }

  const handleProfilePictureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.type.startsWith('image/')) {
        setProfilePicture(file)
        const reader = new FileReader()
        reader.onload = (e) => {
          setPfpPreview(e.target?.result as string)
        }
        reader.readAsDataURL(file)
      } else {
        setErrors(prev => ({ ...prev, pfp: 'Please select an image file for profile picture' }))
      }
    }
  }

  const handleBannerUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        setBannerMedia(file)
        const reader = new FileReader()
        reader.onload = (e) => {
          setBannerPreview(e.target?.result as string)
        }
        reader.readAsDataURL(file)
      } else {
        setErrors(prev => ({ ...prev, banner: 'Please select an image or video file for banner' }))
      }
    }
  }

  const removeProfilePicture = () => {
    setProfilePicture(null)
    setPfpPreview(null)
  }

  const removeBanner = () => {
    setBannerMedia(null)
    setBannerPreview(null)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
           style={{
             background: `
               linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
               radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
               radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
             `,
             backgroundSize: '100% 100%, 800px 800px, 900px 900px'
           }}>
        {/* Metallic shine effect */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent transform -skew-y-1 animate-pulse"></div>
        </div>
        
        <div className="text-center relative z-10">
          <Shield className="w-16 h-16 text-gold-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500">
              Wallet Required
            </span>
          </h1>
          <p className="text-purple-100 mb-8 text-lg">Please connect your wallet to create a profile.</p>
          <button className="px-8 py-3 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-600 transition-colors duration-200">
            Connect Wallet
          </button>
        </div>
      </div>
    )
  }

  // Show existing profile info if user already has one AND not editing
  const urlParams = new URLSearchParams(window.location.search)
  const isEditing = urlParams.get('edit') === 'true'
  
  if (existingProfile && !isEditing) {
    return (
      <div className="min-h-screen relative overflow-hidden"
           style={{
             background: `
               linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
               radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
               radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
             `,
             backgroundSize: '100% 100%, 800px 800px, 900px 900px'
           }}>
        {/* Metallic shine effect */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent transform -skew-y-1 animate-pulse"></div>
        </div>
        
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500">
                Profile Already Exists
              </span>
            </h1>
            <p className="text-purple-100 text-lg">
              You already have a creator profile on HarmonyDataHub.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 shadow-2xl mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-gold-500/20 to-gold-600/20 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-gold-400" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>
                  @{existingProfile.handle}
                </h3>
                <p className="text-purple-200">{existingProfile.displayName}</p>
              </div>
            </div>
            
            {existingProfile.bio && (
              <p className="text-purple-100 mb-4">{existingProfile.bio}</p>
            )}
            
            <div className="flex items-center space-x-2 mb-6">
              <span className="text-sm text-purple-200">Created:</span>
              <span className="text-sm text-white">
                {new Date(existingProfile.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate(`/u/${existingProfile.handle}`)}
              className="flex-1 flex items-center justify-center px-6 py-3 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-600 transition-colors duration-200"
            >
              <User className="w-4 h-4 mr-2" />
              View My Profile
            </button>
            <button
              onClick={() => {
                setExistingProfile(null)
                // Reset form for new profile
                setFormData({
                  handle: '',
                  displayName: '',
                  bio: '',
                  links: { x: '', instagram: '', discord: '', website: '' },
                  nsfw: false
                })
              }}
              className="flex-1 px-6 py-3 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors duration-200"
            >
              Create Another Profile
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden py-12"
         style={{
           background: `
             linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
             radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
             radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
           `,
           backgroundSize: '100% 100%, 800px 800px, 900px 900px'
         }}>
      {/* Metallic shine effect */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent transform -skew-y-1 animate-pulse"></div>
      </div>
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500">
              {new URLSearchParams(window.location.search).get('edit') === 'true' ? 'Edit Your Profile' : 'Create Your Profile'}
            </span>
          </h1>
          <p className="text-purple-100 text-lg">
            {new URLSearchParams(window.location.search).get('edit') === 'true' 
              ? 'Update your creator profile on HarmonyDataHub.'
              : 'Set up your creator profile and join the HarmonyDataHub community.'
            }
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Handle */}
            <div>
              <label htmlFor="handle" className="block text-sm font-medium text-white mb-2">
                Handle <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-200">@</span>
                <input
                  type="text"
                  id="handle"
                  value={formData.handle}
                  onChange={(e) => handleInputChange('handle', e.target.value.toLowerCase())}
                  className={`w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent pl-8 ${errors.handle ? 'border-red-400' : ''}`}
                  placeholder="yourhandle"
                />
              </div>
              {errors.handle && <p className="text-red-400 text-sm mt-1">{errors.handle}</p>}
              <p className="text-purple-200 text-sm mt-1">
                3-20 characters, lowercase letters, numbers, and underscores only
              </p>
            </div>

            {/* Display Name */}
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-white mb-2">
                Display Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="displayName"
                value={formData.displayName}
                onChange={(e) => handleInputChange('displayName', e.target.value)}
                className={`w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent ${errors.displayName ? 'border-red-400' : ''}`}
                placeholder="Your Display Name"
              />
              {errors.displayName && <p className="text-red-400 text-sm mt-1">{errors.displayName}</p>}
            </div>

            {/* Bio */}
            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-white mb-2">
                Bio
              </label>
              <textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => handleInputChange('bio', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                placeholder="Tell your story and what you create..."
              />
            </div>

            {/* Profile Picture Upload */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Profile Picture
              </label>
              {isEditing && !pfpPreview && (
                <div className="mb-4 p-3 bg-white/5 border border-white/20 rounded-lg">
                  <p className="text-purple-200 text-sm">No profile picture currently set</p>
                </div>
              )}
              {pfpPreview ? (
                <div className="relative inline-block">
                  <img
                    src={pfpPreview}
                    alt="Profile preview"
                    className="w-24 h-24 rounded-full object-cover border-2 border-gold-400/50"
                  />
                  <button
                    type="button"
                    onClick={removeProfilePicture}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-white/30 rounded-lg p-6 text-center hover:border-gold-400 transition-colors bg-white/5">
                  <input
                    type="file"
                    onChange={handleProfilePictureUpload}
                    className="hidden"
                    id="pfp-upload"
                    accept="image/*"
                  />
                  <label htmlFor="pfp-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center">
                      <Image className="w-8 h-8 text-gold-400 mb-2" />
                      <p className="text-sm text-purple-100">Upload Profile Picture</p>
                      <p className="text-xs text-purple-200 mt-1">PNG, JPG up to 10MB</p>
                    </div>
                  </label>
                </div>
              )}
              {errors.pfp && <p className="text-red-400 text-sm mt-1">{errors.pfp}</p>}
            </div>

            {/* Banner Upload */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Banner (Image or Video)
              </label>
              {isEditing && !bannerPreview && (
                <div className="mb-4 p-3 bg-white/5 border border-white/20 rounded-lg">
                  <p className="text-purple-200 text-sm">No banner currently set</p>
                </div>
              )}
              {bannerPreview ? (
                <div className="relative">
                  {bannerMedia?.type.startsWith('video/') ? (
                    <video
                      src={bannerPreview}
                      className="w-full h-32 object-cover rounded-lg border-2 border-gold-400/50"
                      controls
                    />
                  ) : (
                    <img
                      src={bannerPreview}
                      alt="Banner preview"
                      className="w-full h-32 object-cover rounded-lg border-2 border-gold-400/50"
                    />
                  )}
                  <button
                    type="button"
                    onClick={removeBanner}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  >
                    <X className="w-3 h-3 text-white" />
                  </button>
                  <div className="mt-2 text-xs text-purple-200">
                    {bannerMedia?.name} ({formatFileSize(bannerMedia?.size || 0)})
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-white/30 rounded-lg p-6 text-center hover:border-gold-400 transition-colors bg-white/5">
                  <input
                    type="file"
                    onChange={handleBannerUpload}
                    className="hidden"
                    id="banner-upload"
                    accept="image/*,video/*"
                  />
                  <label htmlFor="banner-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center">
                      <div className="flex space-x-2 mb-2">
                        <Image className="w-6 h-6 text-gold-400" />
                        <Video className="w-6 h-6 text-gold-400" />
                      </div>
                      <p className="text-sm text-purple-100">Upload Banner</p>
                      <p className="text-xs text-purple-200 mt-1">Image or Video up to 50MB</p>
                    </div>
                  </label>
                </div>
              )}
              {errors.banner && <p className="text-red-400 text-sm mt-1">{errors.banner}</p>}
            </div>

            {/* Social Links */}
            <div>
              <label className="block text-sm font-medium text-white mb-3">
                Social Links
              </label>
              <div className="space-y-3">
                <div>
                  <label htmlFor="website" className="block text-xs text-purple-200 mb-1">Website</label>
                  <div className="relative">
                    <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-200" />
                    <input
                      type="url"
                      id="website"
                      value={formData.links.website}
                      onChange={(e) => handleLinkChange('website', e.target.value)}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent pl-10"
                      placeholder="https://yourwebsite.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="x" className="block text-xs text-purple-200 mb-1">X (Twitter)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-200">@</span>
                    <input
                      type="text"
                      id="x"
                      value={formData.links.x}
                      onChange={(e) => handleLinkChange('x', e.target.value)}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent pl-8"
                      placeholder="username"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="instagram" className="block text-xs text-purple-200 mb-1">Instagram</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-purple-200">@</span>
                    <input
                      type="text"
                      id="instagram"
                      value={formData.links.instagram}
                      onChange={(e) => handleLinkChange('instagram', e.target.value)}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent pl-8"
                      placeholder="username"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="discord" className="block text-xs text-purple-200 mb-1">Discord</label>
                  <input
                    type="text"
                    id="discord"
                    value={formData.links.discord}
                    onChange={(e) => handleLinkChange('discord', e.target.value)}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                    placeholder="server-invite-code"
                  />
                </div>
              </div>
            </div>

            {/* NSFW Toggle */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="nsfw"
                checked={formData.nsfw}
                onChange={(e) => handleInputChange('nsfw', e.target.checked)}
                className="h-4 w-4 text-gold-600 focus:ring-gold-500 border-white/30 rounded bg-white/10"
              />
              <label htmlFor="nsfw" className="ml-2 block text-sm text-white">
                Mark my content as NSFW
              </label>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-4">
                <p className="text-red-400 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex-1 px-6 py-3 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center px-6 py-3 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-600 transition-colors duration-200 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                    {new URLSearchParams(window.location.search).get('edit') === 'true' ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <User className="w-4 h-4 mr-2" />
                    {new URLSearchParams(window.location.search).get('edit') === 'true' ? 'Update Profile' : 'Create Profile'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Wallet Info */}
        <div className="mt-6 p-6 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg">
          <p className="text-sm text-purple-100">
            <strong className="text-gold-400">Connected Wallet:</strong> <span className="text-white">{address?.slice(0, 6)}...{address?.slice(-4)}</span>
          </p>
        </div>
      </div>
    </div>
  )
}
